import { useEscapeKey } from '../../hooks/useEscapeKey.ts';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Users,
  MessageSquare,
  Video,
  Calendar,
  UserPlus,
  Plus,
  Crown,
  Globe,
  Lock,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Eye,
  LayoutGrid,
  Search,
  Phone,
  Trash2,
  Link2,
  Unlink,
  Edit3,
  Check,
  X,
  RotateCcw,
  Sparkles,
  ChevronRight,
  ExternalLink,
  Shield,
  BarChart3,
  LogOut,
  LogIn,
} from 'lucide-react';
import { Space, SpaceMember, SpaceBoard, useTenant } from '../../context/TenantContext.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import { MemberProfilePopover } from './MemberProfilePopover.tsx';
import { ScheduleTeamMeetingModal } from './ScheduleTeamMeetingModal.tsx';
import { CreateSpaceModal } from '../tenant/CreateSpaceModal.tsx';

interface TeamDetailHubProps {
  spaceId: string;
  initialTab?: 'board' | 'boards' | 'members' | 'meetings';
  onBack: () => void;
  onOpenProjectBoard?: (projectId: string) => void;
}

export const TeamDetailHub: React.FC<TeamDetailHubProps> = ({
  spaceId,
  initialTab = 'board',
  onBack,
  onOpenProjectBoard,
}) => {
  const { user, token } = useAuth();
  const { reloadSpaces } = useTenant();
  const { onlineUserIds, subscribe } = useRealtime();

  const isUserOnline = (userId?: string | null): boolean => {
    if (!userId || !onlineUserIds) return false;
    if (onlineUserIds instanceof Set) return onlineUserIds.has(userId);
    if (Array.isArray(onlineUserIds)) return (onlineUserIds as string[]).includes(userId);
    return false;
  };

  const [team, setTeam] = useState<Space | null>(null);
  const [allWorkspaceBoards, setAllWorkspaceBoards] = useState<any[]>([]);
  const [teamTasks, setTeamTasks] = useState<any[]>([]);
  const [teamMeetings, setTeamMeetings] = useState<any[]>([]);
  const [workspaceUsers, setWorkspaceUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'board' | 'boards' | 'members' | 'meetings'>(
    initialTab
  );

  // Kanban Board Filters (Tab 1)
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [selectedBoardFilterId, setSelectedBoardFilterId] = useState<string>('all');
  const [awaitingApprovalOnly, setAwaitingApprovalOnly] = useState(false);
  const [boardSearch, setBoardSearch] = useState('');

  // Member Roster Search (Tab 3)
  const [memberSearch, setMemberSearch] = useState('');

  // Interactive Modals & Popovers
  const [inspectedMember, setInspectedMember] = useState<SpaceMember | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleOneOnOneMember, setScheduleOneOnOneMember] = useState<SpaceMember | null>(null);
  const [isEditTeamModalOpen, setIsEditTeamModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isCreateBoardModalOpen, setIsCreateBoardModalOpen] = useState(false);
  const [isLinkBoardModalOpen, setIsLinkBoardModalOpen] = useState(false);
  const [removingMember, setRemovingMember] = useState<SpaceMember | null>(null);
  const [reassignToUserId, setReassignToUserId] = useState<string>('unassigned');
  const [rejectingTask, setRejectingTask] = useState<any | null>(null);
  // Esc closes whichever dialog is open
  useEscapeKey(
    isAddMemberModalOpen || isCreateTaskModalOpen || isCreateBoardModalOpen || isLinkBoardModalOpen || !!removingMember || !!rejectingTask,
    () => {
      setIsAddMemberModalOpen(false);
      setIsCreateTaskModalOpen(false);
      setIsCreateBoardModalOpen(false);
      setIsLinkBoardModalOpen(false);
      setRemovingMember(null);
      setRejectingTask(null);
    }
  );
  const [rejectionReason, setRejectionReason] = useState('');

  // New Task Form State
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [newTaskAssigneeId, setNewTaskAssigneeId] = useState<string>('');
  const [newTaskDueDate, setNewTaskDueDate] = useState<string>('');
  const [newTaskProjectId, setNewTaskProjectId] = useState<string>('');
  const [isCreatingTask, setIsCreatingTask] = useState(false);

  // New Board Form State
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardKey, setNewBoardKey] = useState('');
  const [newBoardDescription, setNewBoardDescription] = useState('');

  // Add Member Picker State
  const [selectedNewMemberIds, setSelectedNewMemberIds] = useState<string[]>([]);

  const isAdmin = user?.role === 'admin';
  const isViewer = user?.role === 'viewer';
  const isGuest = user?.role === 'guest';
  const isTeamLead = Boolean(team?.isTeamLead || team?.memberRole === 'lead');
  // What each role may do with teams is set on the Roles & Access screen (admin only until it is given)
  const { can } = usePermissions();
  const canEditTeam = can('team.edit');
  const canManageMembers = can('team.members');
  const canAssignLeads = can('team.members');
  const canManageTeam = canEditTeam || canManageMembers;
  const canEditTasks = !isViewer && !isGuest;

  const fetchHubData = useCallback(async () => {
    if (!token || !spaceId) return;
    try {
      const [hubRes, tasksRes, meetingsRes, usersRes] = await Promise.all([
        fetch(`/api/spaces/${encodeURIComponent(spaceId)}/hub`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
        }),
        fetch(`/api/tasks?spaceId=${encodeURIComponent(spaceId)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
        }),
        fetch(`/api/meetings?teamId=${encodeURIComponent(spaceId)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
        }),
        fetch('/api/users', {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
        }),
      ]);

      if (!hubRes.ok) {
        const errData = await hubRes.json().catch(() => ({}));
        setError(errData.error || 'Failed to load team details.');
        setIsLoading(false);
        return;
      }

      const hubData = await hubRes.json();
      setTeam(hubData.space);
      setAllWorkspaceBoards(hubData.allWorkspaceBoards || []);

      if (tasksRes.ok) {
        const tData = await tasksRes.json();
        setTeamTasks(tData.tasks || []);
      }
      if (meetingsRes.ok) {
        const mData = await meetingsRes.json();
        setTeamMeetings(mData.meetings || []);
      }
      if (usersRes.ok) {
        const uData = await usersRes.json();
        setWorkspaceUsers(uData.users || []);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load Team Hub');
    } finally {
      setIsLoading(false);
    }
  }, [token, spaceId, user?.id]);

  useEffect(() => {
    setIsLoading(true);
    fetchHubData();
  }, [fetchHubData]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Real-time refresh when tasks or meetings change
  useEffect(() => {
    const unsubscribe = subscribe((event) => {
      if (
        event.type === 'task_created' ||
        event.type === 'task_updated' ||
        event.type === 'meeting_created' ||
        event.type === 'team_updated'
      ) {
        fetchHubData();
      }
    });
    return () => unsubscribe();
  }, [subscribe, fetchHubData]);

  if (isGuest) {
    return (
      <div className="p-8 text-center">
        <Lock className="w-10 h-10 text-rose-500 mx-auto mb-2" />
        <h2 className="text-lg font-bold text-[var(--text-primary)]">Access Restricted</h2>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          Guest accounts do not have permission to view Teams.
        </p>
      </div>
    );
  }

  if (isLoading && !team) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="w-8 h-8 rounded-full border-3 border-[#026fc1] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center space-y-3">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
        <h3 className="text-base font-bold text-[var(--text-primary)]">
          {error || 'Team not found'}
        </h3>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#026fc1] text-white text-xs font-bold cursor-pointer"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Teams Directory</span>
        </button>
      </div>
    );
  }

  const todayStr = new Date().toISOString().slice(0, 10);
  const isTaskOverdue = (t: any): boolean => {
    if (!t.dueDate || t.status === 'done' || t.status === 'completed') return false;
    return String(t.dueDate).slice(0, 10) < todayStr;
  };

  // Can current user approve a specific task (In Review -> Completed)?
  // Matrix Rule: Approve Tasks (In Review -> Completed) -> Admin, Team Lead, or task Assignor
  const canApproveTask = (task: any): boolean => {
    if (isViewer || isGuest) return false;
    if (isAdmin || isTeamLead) return true;
    const assignorId = task.assignorId || task.creatorId;
    return Boolean(assignorId && assignorId === user?.id);
  };

  const handleTransitionTask = async (
    task: any,
    targetStatus: 'todo' | 'in_progress' | 'in_review' | 'done',
    reason?: string
  ) => {
    if (!canEditTasks) return;
    if (targetStatus === 'done' && !canApproveTask(task)) {
      alert('Only an Admin, Team Lead, or the Task Assignor can approve tasks into Completed.');
      return;
    }

    try {
      const res = await fetch(`/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          status: targetStatus,
          ...(reason ? { rejectionReason: reason } : {}),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to update task status');
        return;
      }
      await fetchHubData();
      await reloadSpaces();
    } catch (e: any) {
      console.error('Failed to transition task:', e);
    }
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle.trim() || !canEditTasks) return;

    // "No project" keeps the task on the team itself; otherwise it goes on the chosen board
    const targetProjId =
      newTaskProjectId === '__NONE__'
        ? ''
        : newTaskProjectId || (team.boards && team.boards[0]?.id) || '';

    try {
      setIsCreatingTask(true);
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          projectId: targetProjId || null,
          spaceId: team.id,
          title: newTaskTitle.trim(),
          description: newTaskDescription.trim(),
          priority: newTaskPriority,
          assigneeId: newTaskAssigneeId || null,
          assignorId: user?.id || null,
          dueDate: newTaskDueDate || null,
          status: 'todo',
        }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-created', { detail: data.task }));
        }
        setNewTaskTitle('');
        setNewTaskDescription('');
        setNewTaskAssigneeId('');
        setNewTaskDueDate('');
        setIsCreateTaskModalOpen(false);
        await fetchHubData();
        await reloadSpaces();
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || 'Failed to create task.');
      }
    } finally {
      setIsCreatingTask(false);
    }
  };

  const handleOpenTeamChat = () => {
    if (isViewer || isGuest) return;
    window.dispatchEvent(
      new CustomEvent('open-floating-chat', {
        detail: {
          channelId: team.chatChannelId || `chan-team-${team.id}`,
          channelName: `#${team.slug}`,
        },
      })
    );
  };

  const handleStartTeamCall = () => {
    if (isViewer || isGuest) return;
    window.dispatchEvent(
      new CustomEvent('start-call', {
        detail: {
          channelId: team.chatChannelId || `chan-team-${team.id}`,
          channelName: `${team.name} Team Huddle`,
          callType: 'video',
        },
      })
    );
  };

  const handleJoinOrLeaveTeam = async () => {
    if (isViewer || isGuest) return;
    const action = team.isJoined ? 'leave' : 'join';
    const res = await fetch(`/api/spaces/${team.id}/${action}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
      },
    });
    if (res.ok) {
      await fetchHubData();
      await reloadSpaces();
    } else {
      const err = await res.json().catch(() => ({}));
      alert(err.error || `Failed to ${action} team`);
    }
  };

  const handleChangeMemberRole = async (targetUserId: string, newRole: 'lead' | 'member') => {
    if (!canAssignLeads) return;
    const res = await fetch(`/api/spaces/${team.id}/members/${targetUserId}/role`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
      },
      body: JSON.stringify({ role: newRole }),
    });
    if (res.ok) {
      await fetchHubData();
      await reloadSpaces();
    } else {
      const err = await res.json().catch(() => ({}));
      alert(err.error || 'Failed to update role');
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!removingMember) return;
    const res = await fetch(`/api/spaces/${team.id}/members/${removingMember.id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
      },
      body: JSON.stringify({ reassignToUserId }),
    });
    if (res.ok) {
      setRemovingMember(null);
      setReassignToUserId('unassigned');
      await fetchHubData();
      await reloadSpaces();
    } else {
      const err = await res.json().catch(() => ({}));
      alert(err.error || 'Failed to remove member');
    }
  };

  const handleAddMembersSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedNewMemberIds.length === 0) return;
    const res = await fetch(`/api/spaces/${team.id}/members`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
      },
      body: JSON.stringify({ userIds: selectedNewMemberIds, role: 'member' }),
    });
    if (res.ok) {
      setSelectedNewMemberIds([]);
      setIsAddMemberModalOpen(false);
      await fetchHubData();
      await reloadSpaces();
    }
  };

  const handleCreateTeamBoard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBoardName.trim()) return;
    const res = await fetch(`/api/spaces/${team.id}/boards/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
      },
      body: JSON.stringify({
        name: newBoardName.trim(),
        key: newBoardKey.trim(),
        description: newBoardDescription.trim(),
      }),
    });
    if (res.ok) {
      setNewBoardName('');
      setNewBoardKey('');
      setNewBoardDescription('');
      setIsCreateBoardModalOpen(false);
      await fetchHubData();
      await reloadSpaces();
    }
  };

  const handleLinkBoard = async (projectId: string) => {
    const res = await fetch(`/api/spaces/${team.id}/boards/link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
      },
      body: JSON.stringify({ projectId }),
    });
    if (res.ok) {
      await fetchHubData();
      await reloadSpaces();
    }
  };

  const handleUnlinkBoard = async (projectId: string) => {
    const res = await fetch(`/api/spaces/${team.id}/boards/unlink`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
      },
      body: JSON.stringify({ projectId }),
    });
    if (res.ok) {
      await fetchHubData();
      await reloadSpaces();
    } else {
      const err = await res.json().catch(() => ({}));
      alert(err.error || 'Cannot unlink board');
    }
  };

  // Filter tasks for the 5-column Team Kanban Board
  const filteredTasks = teamTasks.filter((t) => {
    if (selectedMemberId && t.assigneeId !== selectedMemberId) return false;
    if (selectedBoardFilterId !== 'all' && t.projectId !== selectedBoardFilterId) return false;
    if (awaitingApprovalOnly && t.status !== 'in_review') return false;
    if (boardSearch.trim()) {
      const q = boardSearch.toLowerCase();
      const matchTitle = (t.title || '').toLowerCase().includes(q);
      const matchCode = (t.code || '').toLowerCase().includes(q);
      const matchAssignee = (t.assigneeName || '').toLowerCase().includes(q);
      if (!matchTitle && !matchCode && !matchAssignee) return false;
    }
    return true;
  });

  // Partition into the 5 Kanban Columns: Overdue, To Do, In Progress, In Review, Completed
  const overdueTasks = filteredTasks.filter((t) => isTaskOverdue(t));
  const todoTasks = filteredTasks.filter(
    (t) => (t.status === 'todo' || t.status === 'backlog') && !isTaskOverdue(t)
  );
  const inProgressTasks = filteredTasks.filter(
    (t) => t.status === 'in_progress' && !isTaskOverdue(t)
  );
  const inReviewTasks = filteredTasks.filter(
    (t) => t.status === 'in_review' && !isTaskOverdue(t)
  );
  const completedTasks = filteredTasks.filter(
    (t) => t.status === 'done' || t.status === 'completed'
  );

  const totalAwaitingApproval = teamTasks.filter((t) => t.status === 'in_review').length;
  const members = team.members || [];
  const boards = team.boards || [];

  const activeCallMeeting = teamMeetings.find((m) => m.status === 'in_progress');

  return (
    <div className="flex flex-col h-full bg-[#eaecf0] dark:bg-[#0c0e12] overflow-y-auto">
      {/* Top Team Detail Hub Header & Collaboration Action Bar */}
      <div className="px-5 py-3 bg-white dark:bg-surface-900 border-b border-surface-200 dark:border-surface-800 shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Left: Back Button, Team Avatar, Name, @handle, Visibility, Member Stack */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onBack}
              className="p-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 hover:bg-surface-100 dark:hover:bg-surface-700 border border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-300 transition-colors cursor-pointer shrink-0"
              title="Back to /teams directory"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>

            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shadow-2xs shrink-0"
              style={{ backgroundColor: team.color || '#0162fc' }}
            >
              <Users className="w-5 h-5" />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-base font-extrabold text-surface-900 dark:text-surface-100 tracking-tight truncate">
                  {team.name}
                </h1>
                <span className="px-2 py-0.5 rounded-md bg-brand-500/10 text-brand-600 dark:text-brand-400 font-mono text-xs font-bold">
                  @{team.slug}
                </span>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    team.isPrivate
                      ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                      : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                  }`}
                >
                  {team.isPrivate ? (
                    <>
                      <Lock className="w-2.5 h-2.5" /> Private
                    </>
                  ) : (
                    <>
                      <Globe className="w-2.5 h-2.5" /> Open
                    </>
                  )}
                </span>
              </div>
              <p className="text-xs text-surface-500 dark:text-surface-400 truncate mt-0.5">
                {team.description || `Collaboration hub and 5-column Kanban board for ${team.name}`}
              </p>
            </div>

            <div className="hidden lg:flex items-center -space-x-1.5 ml-3 pl-3 border-l border-surface-200 dark:border-surface-800">
              {members.slice(0, 6).map((m) => {
                const isOnline = isUserOnline(m.id) || m.id === user?.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setInspectedMember(m)}
                    className={`relative w-7 h-7 rounded-full bg-gradient-to-br from-[#0162fc] to-[#4b2efa] text-white text-[11px] font-bold flex items-center justify-center transition-transform hover:scale-110 hover:z-10 cursor-pointer ${
                      m.spaceRole === 'lead'
                        ? 'ring-2 ring-amber-400'
                        : 'ring-2 ring-white dark:ring-surface-900'
                    }`}
                    title={`${m.fullName} (${m.spaceRole === 'lead' ? 'Team Lead' : 'Member'}) — Click for profile`}
                  >
                    {m.avatarUrl ? (
                      <img
                        src={m.avatarUrl}
                        alt={m.fullName}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      (m.fullName || 'U').charAt(0).toUpperCase()
                    )}
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border-1.5 border-white dark:border-surface-900 ${
                        isOnline ? 'bg-emerald-500' : 'bg-surface-400'
                      }`}
                    />
                    {m.spaceRole === 'lead' && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-400 text-slate-950 flex items-center justify-center">
                        <Crown className="w-1.5 h-1.5" />
                      </span>
                    )}
                  </button>
                );
              })}
              {members.length > 6 && (
                <button
                  type="button"
                  onClick={() => setActiveTab('members')}
                  className="w-7 h-7 rounded-full bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 ring-2 ring-white dark:ring-surface-900 text-[9px] font-bold flex items-center justify-center hover:bg-brand-500 hover:text-white transition-colors cursor-pointer"
                >
                  +{members.length - 6}
                </button>
              )}
              {canManageMembers && (
                <button
                  type="button"
                  onClick={() => setIsAddMemberModalOpen(true)}
                  className="w-7 h-7 rounded-full bg-surface-50 dark:bg-surface-800 hover:bg-brand-500 text-surface-400 hover:text-white border border-dashed border-surface-300 dark:border-surface-700 flex items-center justify-center transition-colors cursor-pointer ml-1"
                  title="Add Member to Team"
                >
                  <Plus className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Right: Team Collaboration Action Bar */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {!isViewer && (
              <>
                <button
                  type="button"
                  onClick={handleOpenTeamChat}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-surface-50 hover:bg-brand-50 hover:text-brand-600 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 border border-surface-200 dark:border-surface-700 text-xs font-bold transition-all cursor-pointer"
                  title={`Open #${team.slug} Team Chat`}
                >
                  <MessageSquare className="w-3.5 h-3.5 text-brand-500" />
                  <span>Team Chat</span>
                </button>

                <button
                  type="button"
                  onClick={handleStartTeamCall}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    activeCallMeeting
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white animate-pulse shadow-2xs'
                      : 'bg-surface-50 hover:bg-emerald-50 hover:text-emerald-600 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 border border-surface-200 dark:border-surface-700'
                  }`}
                >
                  <Video
                    className={`w-3.5 h-3.5 ${
                      activeCallMeeting ? 'text-white' : 'text-emerald-500'
                    }`}
                  />
                  <span>{activeCallMeeting ? 'Join Active Call' : 'Start Team Call'}</span>
                </button>

                {(isAdmin || team.isJoined) && (
                  <button
                    type="button"
                    onClick={() => {
                      setScheduleOneOnOneMember(null);
                      setIsScheduleModalOpen(true);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-surface-50 hover:bg-purple-50 hover:text-purple-600 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 border border-surface-200 dark:border-surface-700 text-xs font-bold transition-all cursor-pointer"
                  >
                    <Calendar className="w-3.5 h-3.5 text-purple-500" />
                    <span>Schedule Meeting</span>
                  </button>
                )}

                {(!team.isPrivate || team.isJoined || isAdmin) && (
                  <button
                    type="button"
                    onClick={handleJoinOrLeaveTeam}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                      team.isJoined
                        ? 'bg-surface-50 hover:bg-rose-50 text-surface-500 hover:text-rose-600 dark:bg-surface-800 border-surface-200 dark:border-surface-700'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600'
                    }`}
                  >
                    {team.isJoined ? (
                      <>
                        <LogOut className="w-3.5 h-3.5" />
                        <span>Leave Team</span>
                      </>
                    ) : (
                      <>
                        <LogIn className="w-3.5 h-3.5" />
                        <span>Join Team</span>
                      </>
                    )}
                  </button>
                )}
              </>
            )}

            {(canManageMembers || canEditTeam) && (
              <>
                {canManageMembers && (
                <button
                  type="button"
                  onClick={() => setIsAddMemberModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-surface-50 hover:bg-surface-100 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 border border-surface-200 dark:border-surface-700 text-xs font-bold transition-all cursor-pointer"
                >
                  <UserPlus className="w-3.5 h-3.5 text-brand-500" />
                  <span>+ Add Member</span>
                </button>
                )}

                {canEditTeam && (
                <button
                  type="button"
                  onClick={() => setIsEditTeamModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-surface-50 hover:bg-surface-100 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 border border-surface-200 dark:border-surface-700 text-xs font-bold transition-all cursor-pointer"
                  title="Edit Team Info"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  <span>Edit Team</span>
                </button>
                )}
              </>
            )}

            {canEditTasks && (
              <button
                type="button"
                onClick={() => {
                  setNewTaskProjectId(boards[0]?.id || '');
                  setIsCreateTaskModalOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold shadow-2xs transition-all cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>+ New Task</span>
              </button>
            )}
          </div>
        </div>

        {/* 4 Navigation Tabs Bar */}
        <div className="flex items-center justify-between gap-4 mt-3 pt-3 border-t border-surface-200/80 dark:border-surface-800 overflow-x-auto">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setActiveTab('board')}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'board'
                  ? 'bg-[#121417] text-white dark:bg-white dark:text-black shadow-2xs'
                  : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span>Team Kanban Board</span>
              <span
                className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                  activeTab === 'board'
                    ? 'bg-white/20 text-white dark:bg-black/20 dark:text-black'
                    : 'bg-surface-100 dark:bg-surface-800 text-surface-500'
                }`}
              >
                {teamTasks.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('boards')}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'boards'
                  ? 'bg-[#121417] text-white dark:bg-white dark:text-black shadow-2xs'
                  : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span>Linked Boards & Projects</span>
              <span
                className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                  activeTab === 'boards'
                    ? 'bg-white/20 text-white dark:bg-black/20 dark:text-black'
                    : 'bg-surface-100 dark:bg-surface-800 text-surface-500'
                }`}
              >
                {boards.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('members')}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'members'
                  ? 'bg-[#121417] text-white dark:bg-white dark:text-black shadow-2xs'
                  : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800'
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              <span>Team Members & Roles</span>
              <span
                className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                  activeTab === 'members'
                    ? 'bg-white/20 text-white dark:bg-black/20 dark:text-black'
                    : 'bg-surface-100 dark:bg-surface-800 text-surface-500'
                }`}
              >
                {members.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('meetings')}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'meetings'
                  ? 'bg-[#121417] text-white dark:bg-white dark:text-black shadow-2xs'
                  : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800'
              }`}
            >
              <BarChart3 className="w-3.5 h-3.5" />
              <span>Team Meetings & Workload</span>
              <span
                className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                  activeTab === 'meetings'
                    ? 'bg-white/20 text-white dark:bg-black/20 dark:text-black'
                    : 'bg-surface-100 dark:bg-surface-800 text-surface-500'
                }`}
              >
                {teamMeetings.length}
              </span>
            </button>
          </div>

          {/* Live Health Summary Pills */}
          <div className="hidden xl:flex items-center gap-3 text-xs">
            <span className="text-surface-500 font-semibold">
              <strong className="text-surface-900 dark:text-surface-100">{team.activeTasksCount || 0}</strong>{' '}
              Active
            </span>
            <span className="text-amber-500 font-semibold">
              <strong>{team.inReviewTasksCount || 0}</strong> In Review
            </span>
            <span
              className={`font-semibold ${
                (team.overdueTasksCount || 0) > 0 ? 'text-rose-500' : 'text-surface-400'
              }`}
            >
              <strong>{team.overdueTasksCount || 0}</strong> Overdue
            </span>
            <div className="flex items-center gap-2">
              <div className="w-24 h-2 rounded-full bg-surface-100 dark:bg-surface-800 overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${team.completionPercent || 0}%` }}
                />
              </div>
              <span className="font-bold text-emerald-500">{team.completionPercent || 0}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* TAB 1: 5-COLUMN TEAM KANBAN BOARD (DEFAULT VIEW) */}
      {activeTab === 'board' && (
        <div className="flex-1 flex flex-col p-6 space-y-4 overflow-hidden">
          {/* Interactive Member Filter & Team Lead Approval Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 bg-[var(--bg-panel)] p-3 rounded-2xl border border-[var(--border-color)] shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--text-muted)] mr-1">
                Filter by Member:
              </span>
              <button
                type="button"
                onClick={() => setSelectedMemberId(null)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  !selectedMemberId
                    ? 'bg-[#026fc1] text-white'
                    : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
              >
                All Members ({teamTasks.length})
              </button>

              {members.map((m) => {
                const isSelected = selectedMemberId === m.id;
                const isOnline = isUserOnline(m.id) || m.id === user?.id;
                return (
                  <div
                    key={m.id}
                    className={`inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-xl border text-xs font-bold transition-all ${
                      isSelected
                        ? 'bg-[#026fc1] text-white border-[#026fc1]'
                        : 'bg-[var(--bg-surface)] text-[var(--text-primary)] border-[var(--border-color)] hover:border-[#026fc1]/50'
                    }`}
                  >
                    {/* Clicking avatar opens Member Profile Card Popover */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setInspectedMember(m);
                      }}
                      className="relative w-6 h-6 rounded-full bg-gradient-to-br from-[#026fc1] to-[#00203a] text-white text-[10px] font-bold flex items-center justify-center cursor-pointer"
                      title={`View ${m.fullName}'s profile & quick chat/call actions`}
                    >
                      {(m.fullName || 'U').charAt(0).toUpperCase()}
                      <span
                        className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-[var(--bg-panel)] ${
                          isOnline ? 'bg-emerald-500' : 'bg-slate-400'
                        }`}
                      />
                    </button>

                    {/* Clicking name filters the 5-column Kanban board */}
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedMemberId((prev) => (prev === m.id ? null : m.id))
                      }
                      className="cursor-pointer"
                    >
                      {m.fullName.split(' ')[0]} ({m.activeTasksCount || 0})
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Board Selector if multiple boards linked */}
              {boards.length > 1 && (
                <select
                  value={selectedBoardFilterId}
                  onChange={(e) => setSelectedBoardFilterId(e.target.value)}
                  className="px-3 py-1.5 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs font-bold text-[var(--text-primary)]"
                >
                  <option value="all">All Linked Boards ({boards.length})</option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} ({b.key})
                    </option>
                  ))}
                </select>
              )}

              {/* Team Lead Approval Filter Pill: "Awaiting Approval (X)" */}
              <button
                type="button"
                onClick={() => setAwaitingApprovalOnly((prev) => !prev)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                  awaitingApprovalOnly
                    ? 'bg-amber-500 text-slate-950 border-amber-500 shadow-sm'
                    : 'bg-amber-500/10 text-amber-500 border-amber-500/30 hover:bg-amber-500/20'
                }`}
              >
                <Eye className="w-3.5 h-3.5" />
                <span>Awaiting Approval ({totalAwaitingApproval})</span>
              </button>

              {/* Search Input */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-[var(--text-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={boardSearch}
                  onChange={(e) => setBoardSearch(e.target.value)}
                  placeholder="Search team tasks..."
                  className="pl-8 pr-3 py-1.5 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs text-[var(--text-primary)] w-44"
                />
              </div>
            </div>
          </div>

          {/* 5-Column Kanban Board Grid */}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4 overflow-x-auto pb-2 min-h-[420px]">
            {[
              {
                key: 'overdue',
                title: 'Overdue',
                accent: 'border-rose-500/40 bg-rose-500/5',
                badgeColor: 'bg-rose-500/15 text-rose-500',
                icon: AlertTriangle,
                items: overdueTasks,
              },
              {
                key: 'todo',
                title: 'To Do',
                accent: 'border-[var(--border-color)] bg-[var(--bg-panel)]',
                badgeColor: 'bg-slate-500/15 text-[var(--text-secondary)]',
                icon: Clock,
                items: todoTasks,
              },
              {
                key: 'in_progress',
                title: 'In Progress',
                accent: 'border-sky-500/30 bg-[var(--bg-panel)]',
                badgeColor: 'bg-sky-500/15 text-sky-500',
                icon: Sparkles,
                items: inProgressTasks,
              },
              {
                key: 'in_review',
                title: 'In Review',
                accent: 'border-amber-500/40 bg-amber-500/5',
                badgeColor: 'bg-amber-500/20 text-amber-500',
                icon: Eye,
                items: inReviewTasks,
              },
              {
                key: 'done',
                title: 'Completed',
                accent: 'border-emerald-500/30 bg-[var(--bg-panel)]',
                badgeColor: 'bg-emerald-500/15 text-emerald-500',
                icon: CheckCircle2,
                items: completedTasks,
              },
            ].map((col) => {
              const ColIcon = col.icon;
              return (
                <div
                  key={col.key}
                  className={`flex flex-col rounded-2xl border ${col.accent} overflow-hidden min-w-[250px]`}
                >
                  {/* Column Header */}
                  <div className="px-3.5 py-3 border-b border-[var(--border-color)] flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2">
                      <ColIcon className="w-4 h-4 text-[var(--text-secondary)]" />
                      <h3 className="text-xs font-extrabold uppercase tracking-wider text-[var(--text-primary)]">
                        {col.title}
                      </h3>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-extrabold ${col.badgeColor}`}
                    >
                      {col.items.length}
                    </span>
                  </div>

                  {/* Cards List */}
                  <div className="flex-1 p-3 space-y-2.5 overflow-y-auto">
                    {col.items.length === 0 ? (
                      <div className="h-32 flex items-center justify-center text-center text-[11px] text-[var(--text-muted)] border border-dashed border-[var(--border-color)] rounded-xl p-3">
                        No tasks in {col.title}
                      </div>
                    ) : (
                      col.items.map((task) => {
                        const assigneeMember = members.find((m) => m.id === task.assigneeId);
                        const userCanApprove = canApproveTask(task);

                        return (
                          <div
                            key={task.id}
                            className="p-3 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] hover:border-[#026fc1]/50 shadow-xs space-y-2 transition-all"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-[#026fc1] dark:text-[#38bdf8]">
                                {task.code || 'TASK'}
                              </span>
                              <span
                                className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                                  task.priority === 'urgent'
                                    ? 'bg-rose-500/20 text-rose-500'
                                    : task.priority === 'high'
                                    ? 'bg-amber-500/20 text-amber-500'
                                    : 'bg-slate-500/15 text-[var(--text-muted)]'
                                }`}
                              >
                                {task.priority}
                              </span>
                            </div>

                            <h4 className="text-xs font-bold text-[var(--text-primary)] leading-snug">
                              {task.title}
                            </h4>

                            {task.projectName && (
                              <p className="text-[10px] text-[var(--text-muted)] truncate">
                                Board: <strong className="text-[var(--text-secondary)]">{task.projectName}</strong>
                              </p>
                            )}

                            {/* Assignee & Due Date Row */}
                            <div className="flex items-center justify-between pt-1.5 border-t border-[var(--border-color)]">
                              {assigneeMember ? (
                                <button
                                  type="button"
                                  onClick={() => setInspectedMember(assigneeMember)}
                                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[var(--text-secondary)] hover:text-[#026fc1] cursor-pointer"
                                  title="Click to open member profile card"
                                >
                                  <span className="w-5 h-5 rounded-full bg-[#026fc1] text-white text-[9px] font-bold flex items-center justify-center">
                                    {assigneeMember.fullName.charAt(0).toUpperCase()}
                                  </span>
                                  <span className="truncate max-w-[100px]">
                                    {assigneeMember.fullName.split(' ')[0]}
                                  </span>
                                </button>
                              ) : (
                                <span className="text-[10px] text-[var(--text-muted)] italic">
                                  {task.assigneeName || 'Unassigned'}
                                </span>
                              )}

                              {task.dueDate && (
                                <span
                                  className={`text-[10px] font-semibold ${
                                    isTaskOverdue(task)
                                      ? 'text-rose-500 font-bold'
                                      : 'text-[var(--text-muted)]'
                                  }`}
                                >
                                  {String(task.dueDate).slice(0, 10)}
                                </span>
                              )}
                            </div>

                            {/* Quick Status Transition & Team Lead Review Actions */}
                            {canEditTasks && (
                              <div className="pt-1.5 flex items-center gap-1 flex-wrap">
                                {task.status !== 'in_progress' && task.status !== 'done' && (
                                  <button
                                    type="button"
                                    onClick={() => handleTransitionTask(task, 'in_progress')}
                                    className="px-2 py-1 rounded-lg bg-sky-500/15 hover:bg-sky-500/25 text-sky-500 text-[10px] font-bold cursor-pointer"
                                  >
                                    Start Work
                                  </button>
                                )}

                                {task.status === 'in_progress' && (
                                  <button
                                    type="button"
                                    onClick={() => handleTransitionTask(task, 'in_review')}
                                    className="px-2 py-1 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-500 text-[10px] font-bold cursor-pointer"
                                  >
                                    Submit for Review
                                  </button>
                                )}

                                {task.status === 'in_review' && userCanApprove && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleTransitionTask(task, 'done')}
                                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold cursor-pointer"
                                      title="Approve task and move to Completed"
                                    >
                                      <Check className="w-3 h-3" />
                                      <span>Approve</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setRejectingTask(task);
                                        setRejectionReason('');
                                      }}
                                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-500/15 hover:bg-rose-500/25 text-rose-500 text-[10px] font-bold cursor-pointer"
                                      title="Request changes and move back to In Progress"
                                    >
                                      <RotateCcw className="w-3 h-3" />
                                      <span>Changes</span>
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TAB 2: LINKED BOARDS & PROJECTS */}
      {activeTab === 'boards' && (
        <div className="p-6 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-extrabold text-[var(--text-primary)]">
                Linked Kanban Boards & Projects ({boards.length})
              </h2>
              <p className="text-xs text-[var(--text-muted)]">
                All projects below feed directly into the {team.name} Team Kanban Board and health metrics.
                A team's projects are chosen when the team is created and cannot be added or removed later.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {boards.map((b) => (
              <div
                key={b.id}
                className="p-5 rounded-2xl bg-[var(--bg-panel)] border border-[var(--border-color)] hover:border-[#026fc1]/50 shadow-xs flex flex-col justify-between space-y-4 transition-all"
              >
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-xs shadow-xs"
                        style={{ backgroundColor: b.color || team.color || '#026fc1' }}
                      >
                        {b.key || b.name.slice(0, 3).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-sm font-extrabold text-[var(--text-primary)]">
                          {b.name}
                        </h3>
                        <span className="text-[10px] font-mono text-[var(--text-muted)]">
                          Key: {b.key} · Lead: {b.leadName || 'Assigned Lead'}
                        </span>
                      </div>
                    </div>

                  </div>

                  {b.description && (
                    <p className="text-xs text-[var(--text-muted)] line-clamp-2">
                      {b.description}
                    </p>
                  )}
                </div>

                <div className="space-y-3 pt-2 border-t border-[var(--border-color)]">
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="p-1.5 rounded-lg bg-[var(--bg-surface)]">
                      <span className="block text-xs font-extrabold text-[var(--text-primary)]">
                        {b.activeTasks}
                      </span>
                      <span className="text-[9px] text-[var(--text-muted)] uppercase font-bold">
                        Active
                      </span>
                    </div>
                    <div className="p-1.5 rounded-lg bg-amber-500/10">
                      <span className="block text-xs font-extrabold text-amber-500">
                        {b.inReviewTasks}
                      </span>
                      <span className="text-[9px] text-amber-500 uppercase font-bold">
                        Review
                      </span>
                    </div>
                    <div className="p-1.5 rounded-lg bg-rose-500/10">
                      <span className="block text-xs font-extrabold text-rose-500">
                        {b.overdueTasks}
                      </span>
                      <span className="text-[9px] text-rose-500 uppercase font-bold">
                        Overdue
                      </span>
                    </div>
                    <div className="p-1.5 rounded-lg bg-emerald-500/10">
                      <span className="block text-xs font-extrabold text-emerald-500">
                        {b.completedTasks}
                      </span>
                      <span className="text-[9px] text-emerald-500 uppercase font-bold">
                        Done
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-[var(--text-muted)] font-semibold">Completion</span>
                      <span className="font-bold text-[var(--text-primary)]">
                        {b.progressPercent}%
                      </span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-[var(--bg-surface)] overflow-hidden">
                      <div
                        className="h-full bg-[#026fc1] transition-all"
                        style={{ width: `${b.progressPercent}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBoardFilterId(b.id);
                        setActiveTab('board');
                      }}
                      className="flex-1 py-2 rounded-xl bg-[#026fc1]/10 hover:bg-[#026fc1] text-[#026fc1] hover:text-white text-xs font-bold transition-all cursor-pointer"
                    >
                      Filter Team Board
                    </button>
                    {onOpenProjectBoard && (
                      <button
                        type="button"
                        onClick={() => onOpenProjectBoard(b.id)}
                        className="p-2 rounded-xl bg-[var(--bg-surface)] hover:bg-[var(--bg-app)] text-[var(--text-secondary)] border border-[var(--border-color)] cursor-pointer"
                        title="Open full project workspace"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 3: TEAM MEMBERS & ROLES ROSTER */}
      {activeTab === 'members' && (
        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-extrabold text-[var(--text-primary)]">
                Team Members & Roles Roster ({members.length})
              </h2>
              <p className="text-xs text-[var(--text-muted)]">
                Click any member to open their interactive profile card, or use quick actions for 1-on-1 chat, call, and scheduling.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-[var(--text-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Search team members..."
                  className="pl-8 pr-3 py-2 rounded-xl bg-[var(--bg-panel)] border border-[var(--border-color)] text-xs text-[var(--text-primary)]"
                />
              </div>
              {canManageMembers && (
                <button
                  type="button"
                  onClick={() => setIsAddMemberModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#026fc1] hover:bg-[#025da3] text-white text-xs font-bold cursor-pointer"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  <span>+ Add Member</span>
                </button>
              )}
            </div>
          </div>

          <div className="bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[var(--border-color)] bg-[var(--bg-surface)] text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-muted)]">
                    <th className="py-3 px-4">Member</th>
                    <th className="py-3 px-4">Availability / Capacity</th>
                    <th className="py-3 px-4">Team Role</th>
                    <th className="py-3 px-4">Active | In Review | Overdue</th>
                    <th className="py-3 px-4 text-right">Quick Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-color)] text-xs">
                  {members
                    .filter((m) => {
                      const q = memberSearch.toLowerCase();
                      return (
                        (m.fullName || '').toLowerCase().includes(q) ||
                        (m.email || '').toLowerCase().includes(q) ||
                        (m.username || '').toLowerCase().includes(q)
                      );
                    })
                    .map((m) => {
                      const isOnline = isUserOnline(m.id) || m.id === user?.id;
                      return (
                        <tr
                          key={m.id}
                          className="hover:bg-[var(--bg-surface)]/60 transition-colors"
                        >
                          {/* 1. Member Identity */}
                          <td className="py-3 px-4">
                            <button
                              type="button"
                              onClick={() => setInspectedMember(m)}
                              className="flex items-center gap-3 text-left group cursor-pointer"
                            >
                              <div className="relative">
                                <div
                                  className={`w-9 h-9 rounded-full bg-gradient-to-br from-[#026fc1] to-[#00203a] text-white font-bold flex items-center justify-center text-xs ${
                                    m.spaceRole === 'lead' ? 'ring-2 ring-amber-400' : ''
                                  }`}
                                >
                                  {(m.fullName || 'U').charAt(0).toUpperCase()}
                                </div>
                                <span
                                  className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[var(--bg-panel)] ${
                                    isOnline ? 'bg-emerald-500' : 'bg-slate-400'
                                  }`}
                                />
                              </div>
                              <div>
                                <div className="font-bold text-[var(--text-primary)] group-hover:text-[#026fc1] transition-colors flex items-center gap-1.5">
                                  <span>{m.fullName}</span>
                                  {m.spaceRole === 'lead' && (
                                    <Crown className="w-3.5 h-3.5 text-amber-500" />
                                  )}
                                </div>
                                <div className="text-[11px] text-[var(--text-muted)]">
                                  <span className="font-mono text-[#026fc1]">@{m.username}</span> ·{' '}
                                  {m.email}
                                </div>
                              </div>
                            </button>
                          </td>

                          {/* 2. Availability / Capacity */}
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  isOnline
                                    ? 'bg-emerald-500/15 text-emerald-500'
                                    : 'bg-slate-500/15 text-slate-400'
                                }`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    isOnline ? 'bg-emerald-500' : 'bg-slate-400'
                                  }`}
                                />
                                {isOnline ? 'Online' : 'Offline'}
                              </span>
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  m.workloadStatus === 'High Workload'
                                    ? 'bg-amber-500/15 text-amber-500'
                                    : 'bg-sky-500/15 text-sky-500'
                                }`}
                              >
                                {m.workloadStatus}
                              </span>
                            </div>
                          </td>

                          {/* 3. Team Role (Editable Dropdown for Admin Only) */}
                          <td className="py-3 px-4">
                            {canAssignLeads ? (
                              <select
                                value={m.spaceRole === 'lead' ? 'lead' : 'member'}
                                onChange={(e) =>
                                  handleChangeMemberRole(
                                    m.id,
                                    e.target.value as 'lead' | 'member'
                                  )
                                }
                                className="px-2.5 py-1 rounded-lg bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs font-bold text-[var(--text-primary)] cursor-pointer"
                              >
                                <option value="lead">👑 Team Lead</option>
                                <option value="member">Member</option>
                              </select>
                            ) : (
                              <span
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold ${
                                  m.spaceRole === 'lead'
                                    ? 'bg-amber-500/15 text-amber-500 border border-amber-500/30'
                                    : 'bg-blue-500/10 text-blue-500'
                                }`}
                              >
                                {m.spaceRole === 'lead' ? (
                                  <>
                                    <Crown className="w-3 h-3" /> Team Lead
                                  </>
                                ) : (
                                  'Member'
                                )}
                              </span>
                            )}
                          </td>

                          {/* 4. Task Metrics */}
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2 font-bold">
                              <span className="px-2 py-0.5 rounded bg-[var(--bg-surface)] text-[var(--text-primary)]">
                                {m.activeTasksCount} Active
                              </span>
                              <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-500">
                                {m.inReviewTasksCount} Review
                              </span>
                              <span
                                className={`px-2 py-0.5 rounded ${
                                  m.overdueTasksCount > 0
                                    ? 'bg-rose-500/15 text-rose-500'
                                    : 'bg-[var(--bg-surface)] text-[var(--text-muted)]'
                                }`}
                              >
                                {m.overdueTasksCount} Overdue
                              </span>
                            </div>
                          </td>

                          {/* 5. Quick Actions */}
                          <td className="py-3 px-4 text-right">
                            <div className="inline-flex items-center justify-end gap-1.5">
                              {!isViewer && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      window.dispatchEvent(
                                        new CustomEvent('open-floating-chat', {
                                          detail: {
                                            userId: m.id,
                                            userName: m.fullName,
                                            userAvatar: m.avatarUrl,
                                            userRole:
                                              m.spaceRole === 'lead' ? 'Team Lead' : 'Member',
                                          },
                                        })
                                      )
                                    }
                                    className="px-2.5 py-1.5 rounded-lg bg-[var(--bg-surface)] hover:bg-[#026fc1] text-[var(--text-secondary)] hover:text-white text-[11px] font-bold transition-colors cursor-pointer inline-flex items-center gap-1"
                                    title={`Chat with ${m.fullName}`}
                                  >
                                    <MessageSquare className="w-3 h-3" />
                                    <span>Chat</span>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      window.dispatchEvent(
                                        new CustomEvent('start-call', {
                                          detail: {
                                            targetUserId: m.id,
                                            targetUserName: m.fullName,
                                            targetUserAvatar: m.avatarUrl,
                                            callType: 'audio',
                                          },
                                        })
                                      )
                                    }
                                    className="px-2.5 py-1.5 rounded-lg bg-[var(--bg-surface)] hover:bg-emerald-600 text-[var(--text-secondary)] hover:text-white text-[11px] font-bold transition-colors cursor-pointer inline-flex items-center gap-1"
                                    title={`Call ${m.fullName}`}
                                  >
                                    <Phone className="w-3 h-3" />
                                    <span>Call</span>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setScheduleOneOnOneMember(m);
                                      setIsScheduleModalOpen(true);
                                    }}
                                    className="px-2.5 py-1.5 rounded-lg bg-[var(--bg-surface)] hover:bg-indigo-600 text-[var(--text-secondary)] hover:text-white text-[11px] font-bold transition-colors cursor-pointer inline-flex items-center gap-1"
                                    title={`Schedule 1-on-1 with ${m.fullName}`}
                                  >
                                    <Calendar className="w-3 h-3" />
                                    <span>1-on-1</span>
                                  </button>
                                </>
                              )}

                              {canManageMembers && (canAssignLeads || m.spaceRole !== 'lead') && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRemovingMember(m);
                                    setReassignToUserId('unassigned');
                                  }}
                                  className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-rose-500 hover:bg-rose-500/10 transition-colors cursor-pointer"
                                  title="Remove from Team"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: TEAM MEETINGS & WORKLOAD DISTRIBUTION */}
      {activeTab === 'meetings' && (
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Upcoming Team Meetings */}
          <div className="bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-extrabold text-[var(--text-primary)]">
                  Upcoming Team Meetings ({teamMeetings.length})
                </h3>
                <p className="text-xs text-[var(--text-muted)]">
                  Scheduled standups, sprint reviews, and 1-on-1s for {team.name}
                </p>
              </div>
              {!isViewer && (
                <button
                  type="button"
                  onClick={() => {
                    setScheduleOneOnOneMember(null);
                    setIsScheduleModalOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#026fc1] hover:bg-[#025da3] text-white text-xs font-bold cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>+ Schedule Team Meeting</span>
                </button>
              )}
            </div>

            {teamMeetings.length === 0 ? (
              <div className="py-12 text-center border border-dashed border-[var(--border-color)] rounded-xl space-y-2">
                <Calendar className="w-8 h-8 text-[var(--text-muted)] mx-auto opacity-50" />
                <p className="text-xs font-semibold text-[var(--text-secondary)]">
                  No upcoming meetings scheduled for {team.name}
                </p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {teamMeetings.map((m) => (
                  <div
                    key={m.id}
                    className="p-3.5 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-xs font-bold text-[var(--text-primary)] truncate">
                          {m.title}
                        </h4>
                        <span
                          className={`px-2 py-0.2 rounded-full text-[9px] font-bold uppercase ${
                            m.status === 'in_progress'
                              ? 'bg-emerald-500/20 text-emerald-500'
                              : 'bg-blue-500/15 text-[#026fc1]'
                          }`}
                        >
                          {m.status}
                        </span>
                      </div>
                      <p className="text-[11px] text-[var(--text-muted)]">
                        {new Date(m.scheduledAt).toLocaleString([], {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        · {m.durationMinutes}m · Host: {m.hostName}
                      </p>
                    </div>

                    {!isViewer && (
                      <button
                        type="button"
                        onClick={handleStartTeamCall}
                        className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shrink-0 cursor-pointer"
                      >
                        Join Meeting
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column: Member Workload Distribution Horizontal Capacity Bars */}
          <div className="bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl p-5 space-y-4">
            <div>
              <h3 className="text-sm font-extrabold text-[var(--text-primary)]">
                Member Workload Distribution
              </h3>
              <p className="text-xs text-[var(--text-muted)]">
                Task distribution segmented by status across {team.name} members
              </p>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 text-[10px] font-bold text-[var(--text-muted)]">
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-rose-500" /> Overdue
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-slate-400" /> To Do
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-sky-500" /> In Progress
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-amber-500" /> In Review
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-emerald-500" /> Completed
              </span>
            </div>

            <div className="space-y-3.5">
              {members.map((m) => {
                const total = Math.max(m.totalTasksCount || 0, 1);
                const overduePct = Math.round(((m.overdueTasksCount || 0) / total) * 100);
                const todoPct = Math.round(((m.todoTasksCount || 0) / total) * 100);
                const inProgPct = Math.round(((m.inProgressTasksCount || 0) / total) * 100);
                const inRevPct = Math.round(((m.inReviewTasksCount || 0) / total) * 100);
                const donePct = Math.round(((m.completedTasksCount || 0) / total) * 100);

                return (
                  <div key={m.id} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <button
                        type="button"
                        onClick={() => setInspectedMember(m)}
                        className="font-bold text-[var(--text-primary)] hover:text-[#026fc1] flex items-center gap-1.5 cursor-pointer"
                      >
                        <span>{m.fullName}</span>
                        {m.spaceRole === 'lead' && (
                          <Crown className="w-3 h-3 text-amber-500" />
                        )}
                      </button>
                      <span className="text-[11px] text-[var(--text-muted)] font-semibold">
                        {m.activeTasksCount} active · {m.completedTasksCount} done
                      </span>
                    </div>

                    <div className="w-full h-3 rounded-full bg-[var(--bg-surface)] overflow-hidden flex">
                      {m.totalTasksCount === 0 ? (
                        <div className="w-full h-full bg-slate-500/10" />
                      ) : (
                        <>
                          {overduePct > 0 && (
                            <div
                              className="h-full bg-rose-500"
                              style={{ width: `${overduePct}%` }}
                              title={`Overdue: ${m.overdueTasksCount}`}
                            />
                          )}
                          {todoPct > 0 && (
                            <div
                              className="h-full bg-slate-400"
                              style={{ width: `${todoPct}%` }}
                              title={`To Do: ${m.todoTasksCount}`}
                            />
                          )}
                          {inProgPct > 0 && (
                            <div
                              className="h-full bg-sky-500"
                              style={{ width: `${inProgPct}%` }}
                              title={`In Progress: ${m.inProgressTasksCount}`}
                            />
                          )}
                          {inRevPct > 0 && (
                            <div
                              className="h-full bg-amber-500"
                              style={{ width: `${inRevPct}%` }}
                              title={`In Review: ${m.inReviewTasksCount}`}
                            />
                          )}
                          {donePct > 0 && (
                            <div
                              className="h-full bg-emerald-500"
                              style={{ width: `${donePct}%` }}
                              title={`Completed: ${m.completedTasksCount}`}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Interactive Member Profile Popover */}
      {inspectedMember && (
        <MemberProfilePopover
          member={inspectedMember}
          teamName={team.name}
          onClose={() => setInspectedMember(null)}
          onScheduleOneOnOne={(m) => {
            setScheduleOneOnOneMember(m);
            setIsScheduleModalOpen(true);
          }}
          onViewAssignedTasks={(m) => {
            setSelectedMemberId(m.id);
            setActiveTab('board');
          }}
        />
      )}

      {/* Schedule Team Meeting Modal */}
      <ScheduleTeamMeetingModal
        isOpen={isScheduleModalOpen}
        onClose={() => {
          setIsScheduleModalOpen(false);
          setScheduleOneOnOneMember(null);
        }}
        team={team}
        initialOneOnOneMember={scheduleOneOnOneMember}
        teamTasks={teamTasks}
        onScheduled={() => fetchHubData()}
      />

      {/* Edit Team Modal */}
      <CreateSpaceModal
        isOpen={isEditTeamModalOpen}
        onClose={() => setIsEditTeamModalOpen(false)}
        editingSpace={team}
        onSuccess={() => fetchHubData()}
      />

      {/* Safe Member Removal Modal with Task Reassignment */}
      {removingMember && (
        <div
          className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setRemovingMember(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-rose-500/15 text-rose-500 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-[var(--text-primary)]">
                    Remove {removingMember.fullName} from {team.name}?
                  </h3>
                  <p className="text-xs text-[var(--text-muted)]">
                    Removes member from team roster and #{team.slug} group chat.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setRemovingMember(null)}
                className="p-1 rounded-lg text-[var(--text-muted)] hover:bg-[var(--bg-surface)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-3.5 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] space-y-2">
              <label className="block text-xs font-bold text-[var(--text-primary)]">
                Reassign {removingMember.fullName}'s {removingMember.activeTasksCount || 0} active team task(s) to:
              </label>
              <select
                value={reassignToUserId}
                onChange={(e) => setReassignToUserId(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-[var(--bg-panel)] border border-[var(--border-color)] text-xs font-semibold text-[var(--text-primary)]"
              >
                <option value="unassigned">
                  Leave Unassigned for Team Lead Triage
                </option>
                {members
                  .filter((m) => m.id !== removingMember.id)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.fullName} ({m.spaceRole === 'lead' ? 'Team Lead' : 'Member'})
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRemovingMember(null)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-[var(--text-muted)] hover:bg-[var(--bg-surface)] cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRemoveMember}
                className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold cursor-pointer"
              >
                Remove & Reassign Tasks
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Changes Modal (In Review -> In Progress) */}
      {rejectingTask && (
        <div
          className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setRejectingTask(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4"
          >
            <h3 className="text-sm font-extrabold text-[var(--text-primary)]">
              Send {rejectingTask.code} for Rework: {rejectingTask.title}
            </h3>
            <textarea
              rows={3}
              required
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Provide mandatory review feedback explaining what needs to be revised..."
              className="w-full px-3.5 py-2.5 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs text-[var(--text-primary)]"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setRejectingTask(null)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-[var(--text-muted)]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!rejectionReason.trim()}
                onClick={async () => {
                  await handleTransitionTask(
                    rejectingTask,
                    'in_progress',
                    rejectionReason.trim()
                  );
                  setRejectingTask(null);
                }}
                className="px-4 py-2 rounded-xl bg-amber-500 text-slate-950 text-xs font-bold disabled:opacity-50 cursor-pointer"
              >
                Send for Rework
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {isAddMemberModalOpen && (
        <div
          className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setIsAddMemberModalOpen(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleAddMembersSubmit}
            className="bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-extrabold text-[var(--text-primary)]">
                Add Members to {team.name}
              </h3>
              <button
                type="button"
                onClick={() => setIsAddMemberModalOpen(false)}
                className="p-1 text-[var(--text-muted)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-60 overflow-y-auto divide-y divide-[var(--border-color)] border border-[var(--border-color)] rounded-xl bg-[var(--bg-surface)]">
              {workspaceUsers
                .filter(
                  (u) =>
                    u.role !== 'guest' &&
                    u.role !== 'viewer' &&
                    !members.some((m) => m.id === u.id)
                )
                .map((u) => {
                  const isChecked = selectedNewMemberIds.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      className="px-3.5 py-2.5 flex items-center justify-between cursor-pointer hover:bg-[var(--bg-panel)]"
                    >
                      <div>
                        <p className="text-xs font-bold text-[var(--text-primary)]">
                          {u.fullName}
                        </p>
                        <p className="text-[10px] text-[var(--text-muted)]">{u.email}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() =>
                          setSelectedNewMemberIds((prev) =>
                            prev.includes(u.id)
                              ? prev.filter((id) => id !== u.id)
                              : [...prev, u.id]
                          )
                        }
                        className="w-4 h-4 accent-[#026fc1]"
                      />
                    </label>
                  );
                })}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsAddMemberModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-[var(--text-muted)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={selectedNewMemberIds.length === 0}
                className="px-4 py-2 rounded-xl bg-[#026fc1] text-white text-xs font-bold disabled:opacity-50 cursor-pointer"
              >
                Add Selected ({selectedNewMemberIds.length})
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Create New Task Modal */}
      {isCreateTaskModalOpen && (
        <div
          className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setIsCreateTaskModalOpen(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleCreateTask}
            className="bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-extrabold text-[var(--text-primary)]">
                Create Task in {team.name}
              </h3>
              <button
                type="button"
                onClick={() => setIsCreateTaskModalOpen(false)}
                className="p-1 text-[var(--text-muted)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">
                Project (optional)
              </label>
              <select
                value={newTaskProjectId || (boards[0]?.id ?? '__NONE__')}
                onChange={(e) => setNewTaskProjectId(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs font-bold text-[var(--text-primary)]"
              >
                <option value="__NONE__">No project (team task)</option>
                {boards.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.key})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">
                Task Title *
              </label>
              <input
                type="text"
                required
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                placeholder="e.g., Implement responsive Team Hub header"
                className="w-full px-3.5 py-2 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs text-[var(--text-primary)]"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">
                Description
              </label>
              <textarea
                rows={2}
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs text-[var(--text-primary)]"
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">
                  Assignee
                </label>
                <select
                  value={newTaskAssigneeId}
                  onChange={(e) => setNewTaskAssigneeId(e.target.value)}
                  className="w-full px-2.5 py-2 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs text-[var(--text-primary)]"
                >
                  <option value="">Unassigned</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.fullName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">
                  Priority
                </label>
                <select
                  value={newTaskPriority}
                  onChange={(e) => setNewTaskPriority(e.target.value as any)}
                  className="w-full px-2.5 py-2 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs text-[var(--text-primary)]"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={newTaskDueDate}
                  onChange={(e) => setNewTaskDueDate(e.target.value)}
                  className="w-full px-2.5 py-2 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs text-[var(--text-primary)]"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsCreateTaskModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-[var(--text-muted)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreatingTask || !newTaskTitle.trim()}
                className="px-4 py-2 rounded-xl bg-[#026fc1] text-white text-xs font-bold cursor-pointer"
              >
                {isCreatingTask ? 'Creating...' : 'Create Task'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Create Team Board Modal */}
      {isCreateBoardModalOpen && (
        <div
          className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setIsCreateBoardModalOpen(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleCreateTeamBoard}
            className="bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4"
          >
            <h3 className="text-sm font-extrabold text-[var(--text-primary)]">
              Create New Board for {team.name}
            </h3>
            <input
              type="text"
              required
              value={newBoardName}
              onChange={(e) => setNewBoardName(e.target.value)}
              placeholder="Board Name (e.g., Frontend Sprint Q4)"
              className="w-full px-3.5 py-2 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs text-[var(--text-primary)]"
            />
            <input
              type="text"
              value={newBoardKey}
              onChange={(e) => setNewBoardKey(e.target.value.toUpperCase())}
              placeholder="Board Key (e.g., FEQ4)"
              maxLength={6}
              className="w-full px-3.5 py-2 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs font-mono text-[var(--text-primary)]"
            />
            <textarea
              rows={2}
              value={newBoardDescription}
              onChange={(e) => setNewBoardDescription(e.target.value)}
              placeholder="Board description..."
              className="w-full px-3.5 py-2 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)] text-xs text-[var(--text-primary)]"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsCreateBoardModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-[var(--text-muted)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-xl bg-[#026fc1] text-white text-xs font-bold cursor-pointer"
              >
                Create Board
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Link Existing Board Modal */}
      {isLinkBoardModalOpen && (
        <div
          className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setIsLinkBoardModalOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-extrabold text-[var(--text-primary)]">
                Link Existing Workspace Board to {team.name}
              </h3>
              <button
                type="button"
                onClick={() => setIsLinkBoardModalOpen(false)}
                className="p-1 text-[var(--text-muted)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto divide-y divide-[var(--border-color)] border border-[var(--border-color)] rounded-xl bg-[var(--bg-surface)]">
              {allWorkspaceBoards
                .filter((wb) => !boards.some((b) => b.id === wb.id))
                .map((wb) => (
                  <div
                    key={wb.id}
                    className="px-3.5 py-2.5 flex items-center justify-between"
                  >
                    <div>
                      <p className="text-xs font-bold text-[var(--text-primary)]">
                        {wb.name} ({wb.key})
                      </p>
                      <p className="text-[10px] text-[var(--text-muted)]">
                        {wb.description || 'Workspace board'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        await handleLinkBoard(wb.id);
                        setIsLinkBoardModalOpen(false);
                      }}
                      className="px-3 py-1 rounded-lg bg-[#026fc1] text-white text-xs font-bold cursor-pointer"
                    >
                      Link Board
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDetailHub;
